/**
 * Open Now Component
 * Calculates whether a store is currently open or closed based on
 * Google Places API format opening hours data, and displays a live status badge.
 *
 * Translation strings are passed via data attributes on the element:
 * - data-open: HTML for "Open" label (e.g. "<span>Open</span>")
 * - data-closed: HTML for "Closed" label
 * - data-opens: "Opens" text
 * - data-opens-at: "Opens at" text
 * - data-closing-at: "Closing at" text
 * - data-at: "at" text
 * - data-days: JSON array of full weekday names starting from Sunday
 */
class OpenNow extends HTMLElement {
  /** @type {string[]} */
  #days = [];

  /** @type {boolean} */
  #use12h = false;

  connectedCallback() {
    const script = document.getElementById(this.dataset.src);
    const status = this.querySelector('[ref="status"]');

    if (!script || !status) return;

    /** @type {{ periods?: Array<{ open: { day: number, time: string }, close: { day: number, time: string } }>, weekday_text?: string[] }} */
    let data;

    try {
      data = JSON.parse(script.textContent);
      this.#days = JSON.parse(this.dataset.days || '[]');
      this.#use12h = this.dataset.timeFormat === '12h';
    } catch {
      return;
    }

    if (!data?.periods?.length) return;

    const { isOpen, nextEvent } = this.#getStatus(data.periods);

    status.classList.add(isOpen ? 'is-open' : 'is-closed');
    status.innerHTML = isOpen ? `${this.dataset.open} – ${nextEvent}` : `${this.dataset.closed} – ${nextEvent}`;
  }

  /**
   * Determine open/closed status and next event message.
   *
   * @param {Array<{ open: { day: number, time: string }, close: { day: number, time: string } }>} periods
   * @returns {{ isOpen: boolean, nextEvent: string }}
   */
  #getStatus(periods) {
    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 100 + now.getMinutes();

    const todayPeriod = periods.find((p) => p.open.day === currentDay);

    if (todayPeriod) {
      const openTime = parseInt(todayPeriod.open.time, 10);
      const closeTime = parseInt(todayPeriod.close.time, 10);

      if (currentTime >= openTime && currentTime < closeTime) {
        return {
          isOpen: true,
          nextEvent: `${this.dataset.closingAt} ${this.#formatTime(closeTime)}`,
        };
      }

      if (currentTime < openTime) {
        return {
          isOpen: false,
          nextEvent: `${this.dataset.opensAt} ${this.#formatTime(openTime)}`,
        };
      }
    }

    const next = this.#getNextOpenPeriod(periods, currentDay);

    if (next) {
      const openTime = parseInt(next.open.time, 10);
      const dayName = this.#getDayName(next.open.day);

      return {
        isOpen: false,
        nextEvent: `${this.dataset.opens} ${dayName} ${this.dataset.at} ${this.#formatTime(openTime)}`,
      };
    }

    return { isOpen: false, nextEvent: '' };
  }

  /**
   * Find the next open period after today.
   *
   * @param {Array<{ open: { day: number, time: string }, close: { day: number, time: string } }>} periods
   * @param {number} currentDay
   * @returns {{ open: { day: number, time: string }, close: { day: number, time: string } } | null}
   */
  #getNextOpenPeriod(periods, currentDay) {
    for (let i = 1; i <= 7; i++) {
      const dayIndex = (currentDay + i) % 7;
      const period = periods.find((p) => p.open.day === dayIndex);
      if (period) return period;
    }
    return null;
  }

  /**
   * Format military time (e.g. 1100) as HH:MM or h:MM AM/PM.
   *
   * @param {number} time
   * @returns {string}
   */
  #formatTime(time) {
    const hours = Math.floor(time / 100);
    const minutes = time % 100;

    if (this.#use12h) {
      const period = hours >= 12 ? 'PM' : 'AM';
      const h = hours % 12 || 12;
      return minutes === 0 ? `${h} ${period}` : `${h}:${minutes.toString().padStart(2, '0')} ${period}`;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  /**
   * Get full weekday name from day index (0 = Sunday).
   *
   * @param {number} day
   * @returns {string}
   */
  #getDayName(day) {
    return this.#days[day] || '';
  }
}

if (!customElements.get('open-now')) {
  customElements.define('open-now', OpenNow);
}
